/**
 * Low-level fetch helpers used by the data layer.
 *
 * Responsibilities:
 *   • Safely fetch + parse JSON (never throws, returns a fallback).
 *   • Surface a clear, actionable message in dev when the local
 *     `test/` fixtures haven't been populated yet.
 */

import { dev } from '$app/environment';
import settings from '$lib/config/settings.json';
import { shouldUseLocalData } from './urls';

/**
 * Build-time data-folder selection from `src/lib/config/settings.json`.
 * Never changes while the app is running.
 */
const LOCAL_DATA_FOLDER = settings.dev.localDataFolder;

/**
 * One-shot warning shown in dev when the local `test/` data folder is
 * selected but its files can't be fetched (typically because the folder
 * hasn't been populated yet from the pristine `previous/` snapshot).
 *
 * We deliberately gate this on a module-level flag so a single missing
 * folder doesn't spam the console once per fetched file.
 */
let missingTestDataWarned = false;

function warnMissingTestData(url) {
  if (missingTestDataWarned) return;
  missingTestDataWarned = true;
  console.warn(
    [
      '',
      '╭─ Local test data not found ──────────────────────────────────────────╮',
      `│ Tried to load: ${url}`,
      '│',
      '│ Your app is configured to read from `src/statics/data/test/`,',
      '│ but that folder is empty or missing the expected files.',
      '│',
      '│ Run the data-toolkit CLI to populate it by setting a',
      '│ tournament state:',
      '│',
      '│   pnpm toolkit --command=tournament-state',
      '│',
      '│ or, interactively:',
      '│',
      '│   pnpm toolkit   →  choose "Set tournament state (for testing)"',
      '╰──────────────────────────────────────────────────────────────────────╯',
      '',
    ].join('\n')
  );
}

/**
 * Safely fetch JSON. Returns `fallback` (defaults to null) when the
 * request fails, returns a non-OK status, or returns non-JSON (e.g. an
 * HTML 404 page from the dev server / S3 AccessDenied XML).
 *
 * @param {typeof fetch} fetchFn
 * @param {string} url
 * @param {unknown} [fallback=null]
 */
export async function safeFetchJson(fetchFn, url, fallback = null) {
  try {
    const response = await fetchFn(url);
    if (!response.ok) {
      // Detect the specific case where dev is configured to read from
      // `data/test/` but the folder hasn't been populated. Vite/SvelteKit
      // returns a 404 for the missing static file — surface a clear,
      // actionable prompt instead of just falling back silently.
      if (
        dev &&
        response.status === 404 &&
        shouldUseLocalData() &&
        LOCAL_DATA_FOLDER === 'test' &&
        url.includes('/data/test/')
      ) {
        warnMissingTestData(url);
      } else if (dev) {
        console.warn(
          `[data] ${response.status} fetching ${url} — falling back.`
        );
      }
      return fallback;
    }

    const text = await response.text();
    const trimmed = text.trim();
    if (trimmed === '' || trimmed.startsWith('<')) {
      if (dev)
        console.warn(`[data] Non-JSON response for ${url} — falling back.`);
      return fallback;
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      if (dev)
        console.warn(`[data] JSON parse failed for ${url} — falling back.`, e);
      return fallback;
    }
  } catch (e) {
    if (dev) console.warn(`[data] Fetch failed for ${url} — falling back.`, e);
    return fallback;
  }
}
